import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Snackbar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import toast from 'react-hot-toast';
import { getApiUrl } from '../config';

function Comments() {
  const [comments, setComments] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedComment, setSelectedComment] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyData, setReplyData] = useState({ name: '', email: '', content: '' });

  const fetchComments = async () => {
    try {
      const res = await fetch(getApiUrl('/api/comments'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('خطا در دریافت نظرات');
    }
  };

  const fetchPendingCount = async () => {
    try {
      const res = await fetch(getApiUrl('/api/comments/pending/count'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  useEffect(() => {
    fetchComments();
    fetchPendingCount();
  }, []);

  const handleApprove = async (id) => {
    try {
      const res = await fetch(getApiUrl(`/api/comments/${id}/approve`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        toast.success('نظر تایید شد');
        fetchComments();
        fetchPendingCount();
      }
    } catch (error) {
      console.error('Error approving comment:', error);
      toast.error('خطا در تایید نظر');
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await fetch(getApiUrl(`/api/comments/${id}/reject`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        toast.success('نظر رد شد');
        fetchComments();
        fetchPendingCount();
      }
    } catch (error) {
      console.error('Error rejecting comment:', error);
      toast.error('خطا در رد نظر');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این نظر اطمینان دارید؟')) return;
    
    try {
      const res = await fetch(getApiUrl(`/api/comments/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        toast.success('نظر حذف شد');
        fetchComments();
        fetchPendingCount();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('خطا در حذف نظر');
    }
  };

  const handleViewComment = (comment) => {
    setSelectedComment(comment);
    setViewDialogOpen(true);
  };

  const handleReply = (comment) => {
    setSelectedComment(comment);
    setReplyData({ name: '', email: '', content: '' });
    setReplyDialogOpen(true);
  };

  const handleSubmitReply = async () => {
    if (!replyData.name || !replyData.email || !replyData.content) {
      toast.error('لطفاً تمام فیلدها را پر کنید');
      return;
    }

    try {
      const res = await fetch(getApiUrl('/public/comments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          post_id: selectedComment.post_id,
          parent_id: selectedComment.id,
          name: replyData.name,
          email: replyData.email,
          content: replyData.content
        })
      });

      if (res.ok) {
        toast.success('پاسخ با موفقیت ارسال شد');
        setReplyDialogOpen(false);
        setReplyData({ name: '', email: '', content: '' });
        fetchComments();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ارسال پاسخ');
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('خطا در ارسال پاسخ');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  return (
    <Box sx={{ p: 3, fontFamily: 'Vazir' }}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>
        مدیریت نظرات بلاگ
        {pendingCount > 0 && (
          <Chip 
            label={`${pendingCount} نظر در انتظار تایید`} 
            color="warning" 
            sx={{ ml: 2 }}
          />
        )}
      </Typography>

      <TableContainer component={Paper} sx={{ fontFamily: 'Vazir' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>نام</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>ایمیل</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>پست</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>نظر</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>وضعیت</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>تاریخ</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comments.map((comment) => (
              <TableRow key={comment.id}>
                <TableCell sx={{ fontFamily: 'Vazir' }}>{comment.name}</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir' }}>{comment.email}</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir' }}>{comment.post_title}</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', maxWidth: 200 }}>
                  <Typography 
                    sx={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      fontFamily: 'Vazir'
                    }}
                  >
                    {comment.content}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={comment.status === 'approved' ? 'تایید شده' : comment.status === 'pending' ? 'در انتظار' : 'رد شده'} 
                    color={comment.status === 'approved' ? 'success' : comment.status === 'pending' ? 'warning' : 'error'}
                    sx={{ fontFamily: 'Vazir' }}
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontSize: '0.875rem' }}>
                  {formatDate(comment.created_at)}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleViewComment(comment)}>
                    <VisibilityIcon />
                  </IconButton>
                  {comment.status === 'pending' && (
                    <>
                      <IconButton onClick={() => handleApprove(comment.id)} color="success">
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton onClick={() => handleReject(comment.id)} color="error">
                        <CancelIcon />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={() => handleDelete(comment.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Comment Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>جزئیات نظر</DialogTitle>
        <DialogContent>
          {selectedComment && (
            <Box sx={{ fontFamily: 'Vazir' }}>
              <Typography variant="h6" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                اطلاعات کاربر:
              </Typography>
              <Typography sx={{ fontFamily: 'Vazir' }}>
                نام: {selectedComment.name}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                ایمیل:
              </Typography>
              <Typography sx={{ fontFamily: 'Vazir' }}>
                {selectedComment.email}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                تاریخ ارسال:
              </Typography>
              <Typography sx={{ fontFamily: 'Vazir' }}>
                {formatDate(selectedComment.created_at)}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                پست مربوطه:
              </Typography>
              <Typography sx={{ fontFamily: 'Vazir' }}>
                {selectedComment.post_title}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                متن نظر:
              </Typography>
              <Typography sx={{ fontFamily: 'Vazir', mt: 1, lineHeight: 1.6 }}>
                {selectedComment.content}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                وضعیت:
              </Typography>
              <Typography sx={{ fontFamily: 'Vazir' }}>
                {selectedComment.status === 'approved' ? 'تایید شده' : selectedComment.status === 'pending' ? 'در انتظار' : 'رد شده'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} sx={{ fontFamily: 'Vazir' }}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>پاسخ به نظر</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="نام"
            value={replyData.name}
            onChange={(e) => setReplyData({ ...replyData, name: e.target.value })}
            margin="normal"
            sx={{ fontFamily: 'Vazir' }}
            inputProps={{ style: { fontFamily: 'Vazir' } }}
          />
          <TextField
            fullWidth
            label="ایمیل"
            value={replyData.email}
            onChange={(e) => setReplyData({ ...replyData, email: e.target.value })}
            margin="normal"
            sx={{ fontFamily: 'Vazir' }}
            inputProps={{ style: { fontFamily: 'Vazir' } }}
          />
          <TextField
            fullWidth
            label="پاسخ"
            multiline
            rows={4}
            value={replyData.content}
            onChange={(e) => setReplyData({ ...replyData, content: e.target.value })}
            margin="normal"
            sx={{ fontFamily: 'Vazir' }}
            inputProps={{ style: { fontFamily: 'Vazir' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)} sx={{ fontFamily: 'Vazir' }}>لغو</Button>
          <Button onClick={handleSubmitReply} variant="contained" sx={{ fontFamily: 'Vazir' }}>ارسال پاسخ</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Comments; 